import { useEffect, useMemo, useState } from "react";
import { Resource, Texture } from "pixi.js";
import { Assets } from "@pixi/assets";
import BoundingBox from "../../Domain/Types/BoundingBox.type";
import { useFeatures } from "../../Hooks/useFeatures";
import { GeneIconImageGrp, GeneParkingPlaceIcon, WPInitialText } from "./PlaceIcon.partial";

interface Props {
  placeScale: number;
  placeTypeId: number;
  boundingBox: BoundingBox;
  isSelectable: boolean;
  rotation?: number;
  icon?: string;
  isColleaguesPlace?: boolean;
}

export function PlaceIcon({
  placeScale,
  placeTypeId,
  boundingBox,
  isSelectable,
  rotation = 0,
  icon,
  isColleaguesPlace
}: Readonly<Props>) {
  const [iconImage, setIconImage] = useState<Texture<Resource> | undefined>(undefined);
  const { useParkIconText } = useFeatures();

  const circleRadius = boundingBox.width * placeScale * 0.2 * (isColleaguesPlace ? 2.2 : 1);

  const parkPosY = 0 - (boundingBox.height * placeScale) / 4 - circleRadius / 1.7;
  const basicPosY = 0 - (boundingBox.width * placeScale) / 4 / 2.1 - 16 * placeScale;
  const colleagPosY =
    0 - (boundingBox.width * placeScale) / 4 / 2.1 - 16 * placeScale - circleRadius / 2.6;
  const normalPosY = isColleaguesPlace ? colleagPosY : basicPosY;

  /** might not be used */
  const initialPosY = placeTypeId === 2 || placeTypeId === 4 ? parkPosY : normalPosY;

  async function fetchImage(icon: string) {
    const image = (await Assets.load(icon).catch(() =>
      setIconImage(undefined)
    )) as Texture<Resource>;
    setIconImage(image);
  }

  /**
   * memoized place icon.
   * @description avoids recreating the same TextStyle or Text props on every re-render, which is expensive
   */
  const memoizedPlaceIcon = useMemo(() => {
    // display profile image of booking users
    if (iconImage) {
      return (
        <GeneIconImageGrp
          iconImage={iconImage}
          circleRadius={circleRadius}
          rotation={rotation}
          placeTypeId={placeTypeId}
          placeScale={placeScale}
          boundingBox={boundingBox}
          isSelectable={isSelectable}
        />
      );
    }

    // display initial text of booking usersl
    if (icon) {
      const wpPosY = 0 - 16 * placeScale;
      const otherPosY = 0 - (boundingBox.height * placeScale) / 4;

      // normal workplace
      return (
        <WPInitialText
          icon={icon}
          circleRadius={circleRadius}
          isColleaguesPlace={isColleaguesPlace}
          isSelectable={isSelectable}
          posY={placeTypeId === 1 ? wpPosY : otherPosY}
          initialPosY={initialPosY}
          rotation={rotation}
        />
      );
    }

    // parking place or electric charging place
    if (placeTypeId === 2 || placeTypeId === 4) {
      return (
        <GeneParkingPlaceIcon
          useParkIconText={useParkIconText}
          isSelectable={isSelectable}
          circleRadius={circleRadius}
          placeScale={placeScale}
          boundingBox={boundingBox}
          placeTypeId={placeTypeId}
        />
      );
    }

    return null;
  }, [icon, isColleaguesPlace, circleRadius, iconImage, placeTypeId]);

  useEffect(() => {
    if (icon && icon.length > 2) fetchImage(icon);
    else setIconImage(undefined);
  }, [icon]);

  return (
    <>
      {/* memoized place icon component */}
      {memoizedPlaceIcon}
    </>
  );
}
